<script lang="ts" setup>
import { toRefs } from 'vue';

import { MyOrdersListType } from '@exchange/libs/order/shared-model/src/lib/order-essentials';

import DivTable from '../../components/DivTable.vue';
import InfoMessageBlock from '../../components/InfoMessageBlock.vue';
import OrdersItemDesktop from '../../components/OrdersItemDesktop.vue';
import { useOrdersListActive, useOrdersListDesktop } from '../../useOrdersList';

const props = defineProps({
  authenticatedUser: { type: Boolean, required: true },
  breakpoint: { type: String, required: true },
  dragging: { type: Boolean, required: true },
  marketId: { type: String, required: true },
  showAllMarkets: { type: Boolean, required: true },
  showOrderType: { type: Boolean, required: true },
});

const { breakpoint: breakpointRef, showOrderType: showOrderTypeRef, marketId } = toRefs(props);

const tableSortingKey = 'MY_ORDERS_OPEN_SORTING';
const type = MyOrdersListType.open;

const { activeOrdersDataToShow, getOrderById, getLimitedActiveOrdersDataToShow } = useOrdersListActive(marketId);
const { desktopHeader, saveSorting, cancelOrder } = useOrdersListDesktop(MyOrdersListType.open, tableSortingKey, breakpointRef, showOrderTypeRef);
</script>

<template>
  <div-table
    class="open-orders-list-desktop"
    :class="breakpoint"
    :header="desktopHeader"
    :rows="activeOrdersDataToShow.allOrders"
    :sticky-header="true"
    @header-changed="saveSorting($event)"
  >
    <template #default="{ rows }">
      <template v-for="item in getLimitedActiveOrdersDataToShow(rows)">
        <orders-item-desktop
          v-if="getOrderById(item.orderId)"
          :key="item.key"
          class="open-orders-list-desktop__row"
          :is-current-market-order="marketId === item.instrumentCode"
          :show-order-type="showOrderType"
          :order="getOrderById(item.orderId)"
          :type="type"
          :class="breakpoint"
          :cancel-order="cancelOrder"
          :is-futures="item.instrumentCode.endsWith('_P')"
          testid="open-orders-list-item"
        />
      </template>
      <info-message-block
        v-if="!rows.length"
        class="open-orders-list-desktop__info"
        :market-id="marketId"
        :show-all-markets="showAllMarkets"
        :authenticated-user="authenticatedUser"
      />
    </template>
  </div-table>
</template>

<style lang="scss">
.open-orders-list-desktop {
  /* used in &__row and grid-table header */
  --side-width: minmax(50px, 0.4fr);
  --market-width: minmax(80px, 1fr);
  --type-width: minmax(100px, 1fr);
  --amount-width: minmax(50px, 0.8fr);
  --price-width: minmax(50px, 0.8fr);
  --total-width: minmax(50px, 0.8fr);
  --time-width: minmax(80px, 1fr);
  --status-width: minmax(80px, 1fr);
  --grid-table-columns: var(--time-width) var(--market-width) var(--side-width) var(--type-width) var(--amount-width) var(--price-width) var(--total-width) var(--status-width);

  /** PROFE-110 remove next `grid-template-rows line` */
  grid-template-rows: min-content auto;

  &.hide-type {
    --grid-table-columns: var(--time-width) var(--market-width) var(--side-width) var(--amount-width) var(--price-width) var(--total-width) var(--status-width);
  }

  &.hide-time {
    --grid-table-columns: var(--market-width) var(--side-width) var(--type-width) var(--amount-width) var(--price-width) var(--total-width) var(--status-width);
  }

  &.hide-type.hide-time {
    --grid-table-columns: var(--market-width) var(--side-width) var(--amount-width) var(--price-width) var(--total-width) var(--status-width);
  }

  &.compact-side {
    --side-width: 35px;
  }

  &__row {
    grid-template-columns: var(--grid-table-columns);
  }

  .align {
    &-left {
      text-align: left;
    }

    &-right {
      text-align: right;
    }
  }

  .status {
    padding-left: 15px;
  }
}
</style>
